import { useQuery } from '@apollo/client';
import { GetLatestOversightRatingsByObligationIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import { extractCmaCreatedAt, isLater } from '@/utils/oversightUtils';

export type OversightObligationRatingLeaf = {
  Rating: number | null;
  CreatedAt: string | null;
};

export type OversightRatingsByObligation = Map<
  string,
  OversightObligationRatingLeaf
>;

const EMPTY_MAP: OversightRatingsByObligation = new Map();

/**
 * Fetches the latest oversight rating per obligation for the given obligation
 * Ids, keyed by ObligationId.
 *
 * "Latest" is determined by the linked oversight_assessment's
 * CreatedAtTimestamp — any CMA qualifies regardless of completion status,
 * matching the behaviour of the risk and control oversight hooks.
 *
 * Like risks (and unlike controls), `obligation_oversight_result` has no
 * direct ParentObligationId column — a finding can attach to multiple
 * obligations via the `parents` array_relationship. `obligationParent` is
 * server-filtered to ParentId ∈ $ObligationIds; the client fans the leaf out
 * across each matching parent.
 *
 * Skips the query when oversight viewing is disabled or no obligations are
 * visible.
 */
export const useLatestOversightRatingsByObligations = (params: {
  obligationIds: string[];
  isEnabled: boolean;
}): {
  ratingsByObligation: OversightRatingsByObligation;
  loading: boolean;
} => {
  const { obligationIds, isEnabled } = params;
  const skip = !isEnabled || obligationIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightRatingsByObligationIdsDocument,
    {
      skip,
      variables: { ObligationIds: obligationIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const ratingsByObligation = useMemo<OversightRatingsByObligation>(() => {
    if (!data) {
      return EMPTY_MAP;
    }

    const latestByObligation: OversightRatingsByObligation = new Map();

    for (const row of data.obligation_oversight_result) {
      const createdAt = extractCmaCreatedAt(row.cmaParent);
      const leaf: OversightObligationRatingLeaf = {
        Rating: row.Rating ?? null,
        CreatedAt: createdAt,
      };
      for (const op of row.obligationParent) {
        const existing = latestByObligation.get(op.ParentId);
        if (!existing || isLater(existing.CreatedAt, createdAt)) {
          latestByObligation.set(op.ParentId, leaf);
        }
      }
    }

    return latestByObligation;
  }, [data]);

  return { ratingsByObligation, loading };
};
